'use strict';

/**
 * Module access: may this user open an app that requires a module (e.g. the LIMS micro app requires 'LIMS')?
 *
 * The same decision legacy apps make through @paviasystems/permissions-control checkModulePermission
 * (lib/permissions-control/source/PermissionsControl.js checkEnforcement + checkPermission). Keep the two in step:
 * a user is refused only when the customer's PermissionsEnforcement configuration enforces the module and the user
 * lacks Module-<Module>-Access. A customer that doesn't enforce it lets everyone in.
 *
 * Pure functions, no pict / network, so they test in isolation.
 */

const NOT_ENFORCED = [ 'Default', 'Level' ];

/**
 * Resolve how a customer enforces a module action, from its PermissionsEnforcement configuration
 * (`Configuration/For/PermissionsEnforcement/In/Customer/<IDCustomer>`).
 *
 * @param {any} pConfiguration - the configuration value (may be empty)
 * @param {number} pIDCustomer
 * @param {string} pModule - e.g. 'LIMS'
 * @param {string} [pAction] - defaults to 'Access'
 * @return {string} 'Strict', 'Default', or whatever the configuration names ('Mine', 'Level', 'Hybrid', ...)
 */
function resolveModuleEnforcement(pConfiguration, pIDCustomer, pModule, pAction)
{
	const tmpConfiguration = pConfiguration || {};
	const tmpAction = pAction || 'Access';
	const tmpCustomerIDs = (tmpConfiguration.Configuration && tmpConfiguration.Configuration.CustomerIDs) || [];
	if (Array.isArray(tmpCustomerIDs) && tmpCustomerIDs.includes(pIDCustomer))
	{
		return 'Strict';
	}
	const tmpModules = (tmpConfiguration.EnforcementTypes && tmpConfiguration.EnforcementTypes.Module) || {};
	const fAction = (pItem, pActionName) =>
	{
		const tmpItem = tmpModules[pItem];
		return (tmpItem && tmpItem.Actions) ? tmpItem.Actions[pActionName] : undefined;
	};
	return fAction(pModule, tmpAction) ||
		fAction(pModule, 'DefaultAction') ||
		fAction('DefaultScope', tmpAction) ||
		fAction('DefaultScope', 'DefaultAction') ||
		'Default';
}

/**
 * @param {string} pEnforcement - from resolveModuleEnforcement
 * @param {boolean} pHasPermission - whether the user holds Module-<Module>-Access
 * @return {boolean} true when the user must be refused
 */
function isModuleAccessDenied(pEnforcement, pHasPermission)
{
	if (NOT_ENFORCED.includes(pEnforcement))
	{
		return false;
	}
	if (pEnforcement === 'Mine')
	{
		// Mine compares a record's creator; a module has no record, so legacy refuses.
		return true;
	}
	if (pHasPermission)
	{
		return false;
	}
	return (pEnforcement !== 'Hybrid');
}

module.exports = { resolveModuleEnforcement, isModuleAccessDenied };
